import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  Database,
  RefreshCw,
  Server,
  GitBranch,
  History,
  RotateCcw,
  Cloud,
  Key,
  Terminal,
  ExternalLink,
} from 'lucide-react';
import api from '../../lib/api';

export function DeploymentPanel() {
  const [deploymentSettings, setDeploymentSettings] = useState({});
  const [containerStatus, setContainerStatus] = useState(null);
  const [rollbackVersion, setRollbackVersion] = useState({});
  const [availableVersions, setAvailableVersions] = useState({ backend: [], frontend: [] });
  const [deploymentHistory, setDeploymentHistory] = useState([]);
  const [rollingBack, setRollingBack] = useState(false);

  const loadAvailableVersions = async () => {
    try {
      const response = await api.get('/admin/available-versions');
      setAvailableVersions(response.data);
    } catch (error) {
      toast.error('Failed to load versions');
    }
  };

  const loadDeploymentHistory = async () => {
    try {
      const response = await api.get('/admin/deployment-history');
      setDeploymentHistory(response.data.history || []);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const handleRollback = async (service, version) => {
    if (!window.confirm(`Are you sure you want to rollback ${service} to ${version}?`)) return;
    setRollingBack(true);
    try {
      await api.post('/admin/rollback', { service, version });
      toast.success(`${service} rolled back to ${version}`);
      loadDeploymentHistory();
    } catch (error) {
      toast.error('Rollback failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="deployment-panel">
      <CardHeader>
        <CardTitle className="text-blue-700 flex items-center gap-2">
          <Server className="w-5 h-5" />
          Deployment & CI/CD
        </CardTitle>
        <CardDescription>
          Configure Docker deployment settings, GitHub authentication, and manage rollbacks
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="credentials" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="credentials">Credentials</TabsTrigger>
            <TabsTrigger value="status">Status</TabsTrigger>
            <TabsTrigger value="rollback">Rollback</TabsTrigger>
          </TabsList>

          {/* Credentials Tab */}
          <TabsContent value="credentials" className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3 p-4 border rounded-lg bg-white">
                <h4 className="font-medium flex items-center gap-2">
                  <GitBranch className="w-4 h-4" /> GitHub Container Registry
                </h4>
                <div className="space-y-2">
                  <Label className="text-xs">GitHub Username/Org</Label>
                  <Input
                    placeholder="your-github-username"
                    value={deploymentSettings?.github_username || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, github_username: e.target.value}))}
                    data-testid="input-github-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Personal Access Token (PAT)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="password"
                      placeholder="ghp_xxxxxxxxxxxx"
                      value={deploymentSettings?.github_token || ''}
                      onChange={(e) => setDeploymentSettings(prev => ({...prev, github_token: e.target.value}))}
                      data-testid="input-github-token"
                    />
                    <Button size="sm" variant="outline" onClick={() => window.open('https://github.com/settings/tokens/new?scopes=read:packages,write:packages', '_blank')}>
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Requires read:packages and write:packages scopes</p>
                </div>
              </div>

              <div className="space-y-3 p-4 border rounded-lg bg-white">
                <h4 className="font-medium flex items-center gap-2">
                  <Terminal className="w-4 h-4" /> Production Server SSH
                </h4>
                <div className="space-y-2">
                  <Label className="text-xs">Server Host</Label>
                  <Input
                    placeholder="your-server.com or IP address"
                    value={deploymentSettings?.ssh_host || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, ssh_host: e.target.value}))}
                    data-testid="input-ssh-host"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">SSH Port</Label>
                  <Input
                    type="number"
                    placeholder="22"
                    value={deploymentSettings?.ssh_port || '22'}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, ssh_port: e.target.value}))}
                    data-testid="input-ssh-port"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">SSH Private Key</Label>
                  <Textarea
                    placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n..."}
                    rows={4}
                    className="font-mono text-xs"
                    value={deploymentSettings?.ssh_key || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, ssh_key: e.target.value}))}
                    data-testid="input-ssh-key"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3 p-4 border rounded-lg bg-white">
                <h4 className="font-medium flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Watchtower Settings
                </h4>
                <div className="space-y-2">
                  <Label className="text-xs">Watchtower URL</Label>
                  <Input
                    placeholder="http://your-server:8080"
                    value={deploymentSettings?.watchtower_url || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, watchtower_url: e.target.value}))}
                    data-testid="input-watchtower-url"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Watchtower API Token</Label>
                  <Input
                    type="password"
                    placeholder="your-watchtower-token"
                    value={deploymentSettings?.watchtower_token || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, watchtower_token: e.target.value}))}
                    data-testid="input-watchtower-token"
                  />
                </div>
              </div>

              <div className="space-y-3 p-4 border rounded-lg bg-white">
                <h4 className="font-medium flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" /> Rollback Service
                </h4>
                <div className="space-y-2">
                  <Label className="text-xs">Rollback Service URL</Label>
                  <Input
                    placeholder="http://your-server:8090"
                    value={deploymentSettings?.rollback_url || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, rollback_url: e.target.value}))}
                    data-testid="input-rollback-url"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Rollback Secret</Label>
                  <Input
                    type="password"
                    placeholder="your-rollback-secret"
                    value={deploymentSettings?.rollback_secret || ''}
                    onChange={(e) => setDeploymentSettings(prev => ({...prev, rollback_secret: e.target.value}))}
                    data-testid="input-rollback-secret"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={async () => {
                try {
                  const response = await api.get('/admin/deployment-settings');
                  setDeploymentSettings(response.data);
                  toast.success('Settings loaded');
                } catch {
                  toast.error('Failed to load settings');
                }
              }}>
                <RefreshCw className="w-4 h-4 mr-2" /> Load Saved
              </Button>
              <Button onClick={async () => {
                try {
                  await api.post('/admin/deployment-settings', deploymentSettings);
                  toast.success('Deployment settings saved securely');
                } catch (error) {
                  toast.error('Failed to save settings: ' + (error.response?.data?.detail || error.message));
                }
              }} className="bg-blue-600 hover:bg-blue-700">
                <Key className="w-4 h-4 mr-2" /> Save Credentials
              </Button>
            </div>
          </TabsContent>

          {/* Status Tab */}
          <TabsContent value="status" className="space-y-4 mt-4">
            <div className="grid md:grid-cols-3 gap-4">
              {['backend', 'frontend', 'mongodb'].map((service) => (
                <div key={service} className="p-4 border rounded-lg bg-white text-center">
                  {service === 'mongodb' ? (
                    <Database className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                  ) : (
                    <Cloud className={`w-8 h-8 mx-auto ${service === 'backend' ? 'text-blue-500' : 'text-green-500'} mb-2`} />
                  )}
                  <h4 className="font-medium capitalize">{service}</h4>
                  <Badge variant={containerStatus?.[service]?.status === 'running' ? 'default' : 'destructive'} className="mt-1">
                    {containerStatus?.[service]?.status || 'Unknown'}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">{containerStatus?.[service]?.image || '-'}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={async () => {
                try {
                  const response = await api.get('/admin/container-status');
                  setContainerStatus(response.data);
                  toast.success('Status refreshed');
                } catch {
                  toast.error('Failed to get status - configure rollback service URL first');
                }
              }}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh Status
              </Button>
              <Button onClick={async () => {
                try {
                  await api.post('/admin/trigger-update');
                  toast.success('Update triggered - Watchtower will check for new images');
                } catch {
                  toast.error('Failed to trigger update');
                }
              }} className="bg-green-600 hover:bg-green-700">
                <RefreshCw className="w-4 h-4 mr-2" /> Trigger Update
              </Button>
            </div>
          </TabsContent>

          {/* Rollback Tab */}
          <TabsContent value="rollback" className="space-y-4 mt-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-medium text-amber-700 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> Failsafe Rollback
              </h4>
              <p className="text-sm text-amber-600 mb-4">
                Use this section to rollback to a previous version if an update breaks the application.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {['backend', 'frontend'].map((service) => (
                  <div key={service} className="space-y-3 p-3 bg-white rounded border">
                    <h5 className="font-medium text-sm capitalize">{service} Rollback</h5>
                    <Select
                      value={rollbackVersion?.[service] || ''}
                      onValueChange={(v) => setRollbackVersion(prev => ({...prev, [service]: v}))}
                    >
                      <SelectTrigger><SelectValue placeholder="Select version..." /></SelectTrigger>
                      <SelectContent>
                        {availableVersions?.[service]?.map(v => (
                          <SelectItem key={v.tag} value={v.tag}>{v.tag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      disabled={!rollbackVersion?.[service] || rollingBack}
                      onClick={() => handleRollback(service, rollbackVersion?.[service])}
                    >
                      {rollingBack ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                      Rollback {service}
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-4" onClick={loadAvailableVersions}>
                <RefreshCw className="w-4 h-4 mr-2" /> Load Available Versions
              </Button>
            </div>

            <div className="p-4 border rounded-lg bg-white">
              <h4 className="font-medium flex items-center gap-2 mb-3">
                <History className="w-4 h-4" /> Deployment History
              </h4>
              <ScrollArea className="h-48">
                <div className="space-y-2">
                  {deploymentHistory?.length > 0 ? deploymentHistory.map((entry, i) => (
                    <div key={i} className={`p-2 rounded text-sm ${entry.status === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex justify-between">
                        <span className="font-medium">{entry.action} - {entry.service}</span>
                        <Badge variant={entry.status === 'success' ? 'default' : 'destructive'} className="text-xs">{entry.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{entry.from_version} → {entry.to_version}</p>
                      <p className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</p>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No deployment history available</p>
                  )}
                </div>
              </ScrollArea>
              <Button variant="outline" size="sm" className="mt-2" onClick={loadDeploymentHistory}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh History
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
